import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { Menu, X, Headphones, LogOut, CreditCard, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  // Helper function to determine if a link is active
  const isActiveLink = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.hash === '#made-for-teams';
    }
    return location.pathname === path;
  };

  // Helper function to get link classes with active state
  const getLinkClasses = (path, baseClasses = "font-medium transition-all duration-200") => {
    const isActive = isActiveLink(path);
    return `${baseClasses} ${
      isActive 
        ? 'text-yellow-600 border-b-2 border-yellow-600 pb-1' 
        : 'text-gray-700 hover:text-yellow-600 hover:border-b-2 hover:border-yellow-600/50 pb-1 border-b-2 border-transparent'
    }`;
  };

  // Helper function for mobile menu link classes
  const getMobileLinkClasses = (path) => {
    const isActive = isActiveLink(path);
    return `block px-3 py-2 font-medium transition-all duration-200 ${
      isActive 
        ? 'text-yellow-600 bg-yellow-50 border-l-4 border-yellow-600' 
        : 'text-gray-700 hover:text-yellow-600 hover:bg-yellow-50'
    }`;
  };

  if (MAINTENANCE_MODE && isHomePage) {
    return (
      <nav className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex justify-center h-16">
            <div className="flex items-center">
              <div className="relative ml-4 flex items-center">
                <div className="relative bg-gradient-to-br from-gray-800 via-yellow-600 to-black p-0.5 rounded-xl shadow-lg">
                  <div className="bg-white rounded-[10px] px-4 py-2 flex items-center justify-center">
                    <img 
                      src="/logo.svg" 
                      alt="Postgame Buzz Logo" 
                      className="h-8 w-8 mr-3"
                    />
                    <div className="text-3xl font-black tracking-tight flex items-center">
                      <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-black bg-clip-text text-transparent">
                        POSTGAME
                      </span>
                      <span className="text-yellow-600 ml-0.5">
                        BUZZ
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex items-center group"
              onClick={() => {
                // Scroll to top when logo is clicked
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <div className="relative ml-4 flex items-center">
                {/* Background glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-gray-800/20 to-yellow-600/30 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Logo container with gradient border */}
                <div className="relative bg-gradient-to-br from-gray-800 via-yellow-600 to-black p-0.5 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <div className="bg-white rounded-[10px] px-4 py-2 flex items-center justify-center">
                    <img 
                      src="/logo.svg" 
                      alt="Postgame Buzz Logo" 
                      className="h-8 w-8 mr-3"
                    />
                    <div className="text-3xl font-black tracking-tight flex items-center">
                      <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-black bg-clip-text text-transparent">
                        POSTGAME
                      </span>
                      <span className="text-yellow-600 ml-0.5">
                        BUZZ
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Subtle shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-500"></div>
              </div>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <HashLink smooth to="/#how-it-works" className={getLinkClasses('/')}>
              How It Works
            </HashLink>
            {isAuthenticated ? (
              <>
                <Link to="/teams" className={getLinkClasses('/teams')}>
                  My Teams
                </Link>
                <Link to="/create" className={`${getLinkClasses('/create')} whitespace-nowrap`}>
                  Create Episode
                </Link>
                <Link to="/my-podcasts" className={`${getLinkClasses('/my-podcasts')} whitespace-nowrap`}>
                  My Podcasts
                </Link>
              </>
            ) : (
              <>
                <HashLink smooth to="/#pricing" className={getLinkClasses('/#pricing')}>
                  Pricing
                </HashLink>
              </>
            )}
          </div>
          
          {/* Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  {/* Credits Badge - More compact on medium screens */}
                  <a href="/#pricing" className="flex items-center space-x-1.5 bg-gradient-to-r from-yellow-400/20 to-yellow-500/30 border border-yellow-400/30 px-2.5 md:px-3 py-1.5 rounded-full hover:from-yellow-400/30 hover:to-yellow-500/40 shadow-sm hover:shadow-md transition-all duration-200 group">
                    <div className="relative">
                      <div className="absolute inset-0 bg-yellow-400/20 rounded-full blur-sm group-hover:bg-yellow-400/30 transition-all duration-300"></div>
                      <CreditCard className="h-3.5 w-3.5 md:h-4 md:w-4 text-yellow-700 group-hover:text-yellow-800 relative z-10 group-hover:scale-110 transition-transform" />
                    </div>
                    <span className="text-sm md:text-base font-bold text-gray-900">{user.credits}</span>
                    <span className="text-xs md:text-sm font-medium text-gray-700 hidden sm:inline">Credits</span>
                  </a>
                  
                  {/* User Profile Section - More compact */}
                  <div className="relative group">
                    <div className="flex items-center space-x-2 md:space-x-3 bg-gradient-to-r from-royal-blue/5 to-royal-blue/10 border border-royal-blue/20 px-2.5 md:px-3 py-1.5 rounded-full hover:from-royal-blue/10 hover:to-royal-blue/20 transition-all duration-200 cursor-pointer">
                      {/* Avatar */}
                      <div className="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-br from-royal-blue to-royal-blue/80 rounded-full flex items-center justify-center text-white font-semibold text-xs md:text-sm shadow-sm">
                        {user.firstName ? user.firstName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      
                      {/* User Info - Hide on smaller screens */}
                      <div className="hidden md:flex flex-col">
                        <span className="text-xs md:text-sm font-semibold text-gray-800 leading-tight whitespace-nowrap">
                          {user.firstName || 'User'}
                        </span>
                        <span className="text-[10px] md:text-xs text-gray-500 leading-tight">
                          Premium
                        </span>
                      </div>
                      
                      <ChevronDown className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                    </div>
                    
                    {/* Dropdown Menu */}
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-1 group-hover:translate-y-0">
                      <div className="py-2">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-semibold text-gray-800">{user.firstName} {user.lastName}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                        <a href={`${API_BASE_URL}/auth/logout`} className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">
                          <LogOut className="h-4 w-4" />
                          <span>Sign Out</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <a 
                    href={`${API_BASE_URL}/auth/google`} 
                    className="px-3 sm:px-4 py-1.5 text-sm sm:text-base font-medium text-gray-700 hover:text-royal-blue transition-colors whitespace-nowrap hover:bg-gray-50 rounded-lg"
                  >
                    Sign In
                  </a>
                  <div className="h-6 w-px bg-gray-200"></div>
                  <Link 
                    to="/create" 
                    className="btn-primary flex items-center space-x-1.5 px-3 sm:px-4 py-1.5 text-sm sm:text-base whitespace-nowrap"
                  >
                    <span>Get Started</span>
                    <Headphones className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  </Link>
                </div>
              )}
            </div>

          {/* Mobile menu button - Show earlier on smaller screens */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-royal-blue"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Show on small screens */}
        {isOpen && (
          <div className="md:hidden">
                        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
                            <HashLink smooth to="/#how-it-works" className={getMobileLinkClasses('/')} onClick={() => setIsOpen(false)}>
                How It Works
              </HashLink>
              {isAuthenticated && (
                <>
                  <Link to="/teams" className={getMobileLinkClasses('/teams')} onClick={() => setIsOpen(false)}>
                    My Teams
                  </Link>
                  <Link to="/create" className={getMobileLinkClasses('/create')} onClick={() => setIsOpen(false)}>
                    Create Episode
                  </Link>
                  <Link to="/my-podcasts" className={getMobileLinkClasses('/my-podcasts')} onClick={() => setIsOpen(false)}>
                    My Podcasts
                  </Link>
                </>
              )}
              <div className="border-t my-2"></div>
              {isAuthenticated ? (
                <>
                  <div className="px-3 py-3 bg-gradient-to-r from-royal-blue/5 to-royal-blue/10 rounded-lg mx-2">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-royal-blue to-royal-blue/80 rounded-full flex items-center justify-center text-white font-semibold shadow-sm">
                        {user.firstName ? user.firstName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{user.firstName}</p>
                        <p className="text-xs text-gray-500">Premium Member</p>
                      </div>
                    </div>
                    <a href="/#pricing" className="flex items-center space-x-2 bg-gradient-to-r from-electric-green/10 to-electric-green/20 border border-electric-green/20 px-3 py-2 rounded-full hover:from-electric-green/20 hover:to-electric-green/30 transition-all">
                      <CreditCard className="h-4 w-4 text-electric-green" />
                      <span className="font-bold text-gray-800">{user.credits}</span>
                      <span className="text-sm text-gray-600">Credits</span>
                    </a>
                  </div>
                  <a href={`${API_BASE_URL}/auth/logout`} className="flex items-center space-x-2 mx-2 px-3 py-2 text-red-600 hover:bg-red-50 font-medium rounded-lg transition-colors">
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </a>
                </>
              ) : (
                <>
                  <HashLink smooth to="/#pricing" className={getMobileLinkClasses('/#pricing')} onClick={() => setIsOpen(false)}>
                    Pricing
                  </HashLink>
                  <div className="border-t my-2"></div>
                  <a href={`${API_BASE_URL}/auth/google`} className="block w-full text-left px-3 py-2 text-gray-700 hover:text-royal-blue font-medium">
                    Login
                  </a>
                  <Link to="/create" className="block w-full text-center mt-2 btn-primary" onClick={() => setIsOpen(false)}>
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
